import { BigDecimal } from "@solomon-labs/bigdecimal";
import {
    ArrowRightLongIcon,
    ArrowShortUpRightIcon
} from "@solomon-labs/design/components/icons";
import { Button } from "@solomon-labs/design/components/ui";
import React from "react";

import { ConnectorLine } from "../../components/Brand/ConnectorLine";
import { useStakeFlowEntrance } from "../../hooks/useStakeFlowEntrance";
import type { YaasPositionCardData } from "../../types/yaas";

import { YaasEarningsProjectionCard } from "./YaasEarningsProjectionCard";
import { YaasPreviewBalanceCard } from "./YaasPreviewBalanceCard";

/** A projection needs a balance, not an enrolled position or earned rewards.
 *  Full positions remain compatible for explicit demo/sim consumers. */
export interface YaasProjectionPosition {
    position: Pick<
        YaasPositionCardData["position"],
        "USDvValue" | "formattedUSDvValue" | "usdvAmountExact"
    >;
}

interface YaasEarnProjectionCTAProps {
    /** Known wallet balance, or an illustrative balance in a demo. */
    position: YaasProjectionPosition;
    /** Label shown above the balance number. Defaults to "Account
     *  Balance"; the disconnected preview overrides to "Holding"
     *  since there's no real account behind it. */
    balanceLabel: string;
    /** Primary CTA copy — varies by surface state:
     *    - "Start Earning" (connected, balance ≥ min)
     *    - "Get More USDv to Earn" (connected, balance < min)
     *    - "See How Much You Can Earn" (disconnected) */
    primaryLabel: string;
    /** Whether to show a right-long arrow on the primary CTA. True
     *  for the disconnected "See How Much You Can Earn" case so the
     *  CTA reads as a forward-marketing nudge. */
    primaryArrow: boolean;
    primaryLinkOut?: boolean;
    balanceUnavailable?: boolean;
    balanceNotice?: React.ReactNode;
    earningsLoading?: boolean;
    actionsDisabled?: boolean;
    /** Keep the enrollment action disabled while balance discovery finishes. */
    primaryDisabled?: boolean;
    /** Whether to render the secondary "Get More USDv" link next to
     *  the primary CTA. False when the primary IS the buy CTA. */
    showGetMoreUsdv: boolean;
    /** Solomon's advertised APY — used to compute the yearly value
     *  for the count-up. Null until the rate loads; the figure then
     *  shows the loading skeleton instead of a projection. */
    yaasApy: number | null;
    /** Primary CTA click handler. */
    onEnable: () => void;
    /** Secondary "Get More USDv" link click handler. Only consumed
     *  when `showGetMoreUsdv` is true. */
    onGetMoreUsdv?: () => void;
}

/**
 * Earn-projection CTA — used when the wallet is connected with USDv
 * but the user hasn't enrolled in YaaS yet (or is below the minimum
 * balance and needs to top up first). Stacks the position card
 * (noPosition, ghost variant with no distribution rows) over an
 * earnings-projection card, then a primary CTA + an optional
 * secondary "Get More USDv" link.
 *
 * Mirrors the Stake-flow microinteraction: an ember outline traces
 * in around the Account Balance card, a line drops down to the
 * Earns You card, the Earns You card bumps, and the yearly value
 * counts up from $0. Plays once on mount.
 *
 * Timing (mirrors `useStakeAnimation`):
 *   t=0     border-in (top + sides + bottom, staged 0/200/400ms)
 *   t=600   line-in (75ms)
 *   t=675   bump (200ms) + count-up (600ms)
 *   t=875   line-out (200ms)
 *   t=1075  border-out — ember edges retract so the card settles
 *           back to its neutral `border-line-base` +
 *           `border-t-accent-base` resting state (top-out 300ms,
 *           sides-out 300ms @300ms, bottom-out 300ms @600ms = ~900ms
 *           total)
 *   t=1975  overlay unmounts entirely — natural card border showing
 *
 * Mechanism-only / SDK-free. Hoisted into `@solomon-apps/shared`
 * (widgets/yaas) so the sim-mode Enable Rewards preview frame +
 * external (docs/wiki) consumers can render it. apps/main re-exports
 * it from its old path via a shim.
 */
export function YaasEarnProjectionCTA({
    position,
    balanceLabel,
    primaryLabel,
    primaryArrow,
    primaryLinkOut = false,
    balanceUnavailable = false,
    balanceNotice,
    earningsLoading = false,
    actionsDisabled = false,
    primaryDisabled = false,
    showGetMoreUsdv,
    yaasApy,
    onEnable,
    onGetMoreUsdv
}: YaasEarnProjectionCTAProps): React.ReactElement {
    const principal =
        position.position.usdvAmountExact === undefined
            ? new BigDecimal(position.position.USDvValue)
            : new BigDecimal(position.position.usdvAmountExact ?? "0");
    const yearly =
        yaasApy === null ? null : principal.multiply(yaasApy).divide(100);

    // borderInTotalMs=700 / borderOutTotalMs=700 match the
    // BorderAnimation YaasPositionCard renders below with
    // `traceSideSpeed=300` (top 200 + sides 300 + bottom 200).
    const { borderIn, borderOut, lineIn, lineOut, bump, countEnabled } =
        useStakeFlowEntrance();

    return (
        <div className="flex flex-col gap-4">
            <YaasPreviewBalanceCard
                formatted={position.position.formattedUSDvValue}
                eyebrow={balanceLabel}
                traceBorderIn={borderIn}
                traceBorderOut={borderOut}
                traceTriggerKey={borderOut ? 2 : 1}
                traceSideSpeed={300}
                unavailable={balanceUnavailable}
                notice={balanceNotice}
            />

            {/* Connector line — sits inside the gap-4 (16px) between
                the two cards via negative margins so mounting/unmounting
                doesn't shift layout. */}
            {(lineIn || lineOut) && (
                <div
                    className="flex justify-center"
                    style={{
                        marginTop: -16,
                        marginBottom: -16,
                        height: 16
                    }}
                >
                    <ConnectorLine
                        direction={lineOut ? "out" : "in"}
                        color="accent"
                    />
                </div>
            )}

            {/* Earns You card — bumps when the line lands; the yearly
                value counts up from $0 over 600ms in parallel. */}
            <YaasEarningsProjectionCard
                yearlyValue={yearly?.toNumber() ?? 0}
                countEnabled={countEnabled}
                bumpTriggered={bump}
                loading={earningsLoading || yearly === null}
            />

            {/* Primary CTA — sits with extra breathing room (parent
                gap-4 + mt-2 = 24px) below the earnings card so the
                action reads as a separate decision step. Matches the
                gap between the position card and the Manage Positions
                row in the post-enrollment surface. */}
            <div className="flex flex-col md:flex-row md:items-center gap-3 self-stretch md:self-start w-full md:w-auto mt-2">
                <Button
                    variant="raised"
                    interaction="lifted"
                    size="lg"
                    fullWidth
                    disabled={actionsDisabled || primaryDisabled}
                    onClick={onEnable}
                    className="md:w-auto"
                    // Sim-cursor anchor: the enable-rewards sim's scripted
                    // cursor travels here to "click" Start Earning. Inert
                    // attribute — no effect outside a sim looking for the key.
                    data-sim-cursor-key="start-earning"
                >
                    {primaryLabel}
                    {primaryLinkOut && (
                        <ArrowShortUpRightIcon className="size-6" />
                    )}
                    {primaryArrow && <ArrowRightLongIcon className="size-6" />}
                </Button>
                {showGetMoreUsdv && (
                    <Button
                        variant="sunken"
                        interaction="lifted"
                        outline
                        size="lg"
                        fullWidth
                        disabled={actionsDisabled}
                        onClick={onGetMoreUsdv}
                        className="md:w-auto"
                    >
                        Swap USDv
                        <ArrowRightLongIcon className="size-6" />
                    </Button>
                )}
            </div>
        </div>
    );
}
